import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, CheckCircle, XCircle, Clock, ChevronRight } from 'lucide-react';
import { auth } from '../../firebase';
import enhancedUserService from '../../services/enhancedUserService';
import LogamiLogoSpinner from '../ui/LogamiLogoSpinner';
import { PracticeWritingFeedback } from './writing/WritingFeedbackPanel';

const MODE_LABELS = {
  reading: 'Lesen',
  listening: 'Hören',
  writing: 'Schreiben',
};

function formatDate(timestamp) {
  if (!timestamp?.toDate) return '';
  return timestamp.toDate().toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAnswerValue(value) {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function scoreBadgeClasses(pct) {
  if (pct >= 80) return 'text-emerald-700 bg-emerald-100 border-emerald-300';
  if (pct >= 60) return 'text-amber-700 bg-amber-100 border-amber-300';
  return 'text-rose-700 bg-rose-100 border-rose-300';
}

const PracticeHistoryPanel = ({ mode, provider, level, onClose, filterAttempt }) => {
  const { t } = useTranslation();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) { setLoading(false); return; }
    setLoading(true);
    const load = mode === 'writing'
      ? enhancedUserService.loadPracticeWritingHistory(uid, provider, level)
      : enhancedUserService.getPracticeAttemptsHistory(uid, { mode, provider, level });
    load.then(result => {
      let list = Array.isArray(result) ? result : [];
      if (filterAttempt) list = list.filter(filterAttempt);
      setAttempts(list);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, provider, level]);

  const handleBack = () => {
    if (selectedAttempt) { setSelectedAttempt(null); return; }
    onClose();
  };

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-10 animate-fade-in">
      <div className="max-w-3xl mx-auto px-4 py-5 sm:py-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-xl border border-[#E1E4E8] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-lg transition-all duration-300 touch-manipulation"
            aria-label={t('common.goBack', { defaultValue: 'Back' })}
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="font-space text-xl sm:text-2xl font-black text-gray-900">
            {MODE_LABELS[mode] || ''} · {t('exam.training.historyTitle', { defaultValue: 'Practice History' })}
          </h1>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <LogamiLogoSpinner size="lg" decorative={false} alt="Logami loading" />
            <p className="mt-4 text-sm text-gray-500 font-semibold">
              {t('common.loading', { defaultValue: 'Wird geladen ...' })}
            </p>
          </div>
        ) : selectedAttempt ? (
          <AttemptDetail mode={mode} attempt={selectedAttempt} />
        ) : (
          <AttemptList mode={mode} attempts={attempts} onSelect={setSelectedAttempt} t={t} />
        )}
      </div>
    </div>
  );
};

function AttemptList({ mode, attempts, onSelect, t }) {
  if (attempts.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-gray-200 p-10 text-center">
        <Clock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-semibold">
          {t('exam.training.noAttemptsYet', { defaultValue: 'Noch keine Übungsversuche vorhanden.' })}
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {attempts.map(attempt => (
        <li key={attempt.id}>
          <button
            onClick={() => onSelect(attempt)}
            className="w-full flex items-center gap-4 rounded-2xl border border-[#E1E4E8] bg-white p-4 text-left shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-lg transition-all duration-300 touch-manipulation"
          >
            {mode === 'writing' ? (
              <WritingRowBadge attempt={attempt} />
            ) : (
              <ReadingListeningRowBadge attempt={attempt} />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-900 truncate">
                {mode === 'writing'
                  ? (attempt.partName || attempt.taskType || '—')
                  : `${attempt.sampleLabel || ''}${attempt.sampleLabel && attempt.partName ? ' · ' : ''}${attempt.partName || ''}`}
              </p>
              <p className="text-xs text-gray-400 font-medium mt-0.5">
                {formatDate(attempt.completedAt || attempt.savedAt)}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function ReadingListeningRowBadge({ attempt }) {
  const total = attempt.maxScore || 0;
  const pct = total > 0 ? Math.round(((attempt.score || 0) / total) * 100) : 0;
  return (
    <div className={`flex-shrink-0 min-w-[64px] text-center px-2.5 py-1.5 rounded-xl border font-bold text-sm ${scoreBadgeClasses(pct)}`}>
      {attempt.score ?? 0}/{total}
    </div>
  );
}

function WritingRowBadge({ attempt }) {
  if (!attempt.feedback) {
    // Pending (correction still running) or failed entry
    const failed = attempt.status === 'failed';
    return (
      <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border ${failed ? 'text-rose-700 bg-rose-100 border-rose-300' : 'text-amber-700 bg-amber-100 border-amber-300'}`}>
        {failed ? <XCircle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
      </div>
    );
  }
  const passed = attempt.feedback?.passed;
  const classes = passed === true
    ? 'text-emerald-700 bg-emerald-100 border-emerald-300'
    : passed === false
    ? 'text-rose-700 bg-rose-100 border-rose-300'
    : 'text-purple-700 bg-purple-100 border-purple-300';
  return (
    <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl border font-bold text-base ${classes}`}>
      {passed === true ? '✓' : passed === false ? '✗' : '∑'}
    </div>
  );
}

function WritingAttemptWithoutFeedback({ attempt }) {
  const { t } = useTranslation();
  const failed = attempt.status === 'failed';
  return (
    <div className="space-y-4">
      <div className={`rounded-2xl border p-4 flex items-start gap-3 ${failed ? 'border-rose-200 bg-rose-50/60' : 'border-amber-200 bg-amber-50/60'}`}>
        {failed
          ? <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
          : <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />}
        <div>
          <p className="text-sm font-bold text-gray-900">
            {failed
              ? t('exam.training.correctionFailed', { defaultValue: 'Correction failed' })
              : t('exam.training.correctionPending', { defaultValue: 'Correction in progress…' })}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            {failed
              ? t('exam.training.correctionFailedInfo', { defaultValue: 'This correction could not be completed. Your text is saved — you can submit it again from the writing training.' })
              : t('exam.training.correctionPendingInfo', { defaultValue: 'Your text was submitted and is being corrected. Check back in a moment — the feedback will appear here.' })}
          </p>
        </div>
      </div>
      {Object.entries(attempt.writingData?.texts || {}).map(([key, taskData]) => (
        <div key={key} className="rounded-2xl border border-[#E1E4E8] bg-white p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">
            {t('exam.training.yourSubmittedText', { defaultValue: 'Your submitted text' })}
          </p>
          <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{taskData?.text}</p>
        </div>
      ))}
    </div>
  );
}

function AttemptDetail({ mode, attempt }) {
  if (mode === 'writing') {
    if (!attempt.feedback) {
      return <WritingAttemptWithoutFeedback attempt={attempt} />;
    }
    return <PracticeWritingFeedback feedback={attempt.feedback} writingData={attempt.writingData} />;
  }

  return (
    <div className="space-y-3">
      {(attempt.answers || []).map((q, idx) => (
        <div
          key={q.questionId || idx}
          className={`rounded-2xl border p-4 ${q.isCorrect ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}
        >
          <div className="flex items-start gap-3">
            {q.isCorrect
              ? <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              : <XCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">Frage {q.questionNumber ?? idx + 1}</p>
              <p className="text-sm text-gray-700 mt-1">
                <span className="text-gray-500">Ihre Antwort:</span> {formatAnswerValue(q.userAnswer)}
              </p>
              {!q.isCorrect && (
                <p className="text-sm text-gray-700 mt-0.5">
                  <span className="text-gray-500">Richtig:</span> {formatAnswerValue(q.correctAnswer)}
                </p>
              )}
              {q.explanation && (
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  {typeof q.explanation === 'object' ? (q.explanation.de || q.explanation.en || '') : q.explanation}
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default PracticeHistoryPanel;
